import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Checkbox, Form, InputNumber, Popover } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { getCycleRangeText, transformBudgetAllCycles } from '../../utils/BudgetUtils';
import { getDateOnlyStartOfDayFromBEDateString } from '../../utils/DataFormatterUtils';
import { cn } from '../../utils/ClassNamesUtils';
import './BudgetForEachCycleFormField.less';

const BudgetForEachCycleFormField: FC<any> = ({
	form,
	currency,
	initialValue,
}): React.ReactElement => {
	const { setFieldsValue } = form;

	const allCycles = Form.useWatch('allCycles', form);
	const budgetPerPeriod = Form.useWatch('budgetPerPeriod', form);

	const [popoverVisibleIndex, setPopoverVisibleIndex] = useState<number | null>(null);
	const [selectedPeriods, setSelectedPeriods] = useState<number[]>([]);

	useEffect(() => {
		if (allCycles && allCycles.length) {
			setFieldsValue({
				budgetPerPeriod: transformBudgetAllCycles(allCycles).map((cycle) => {
					const existingBudgetValue = (initialValue || []).find((_) => _.cycle === cycle.cycle);
					return {
						...cycle,
						budget: nullSafeGet('budget', existingBudgetValue),
					};
				}),
			});
		}
	}, [allCycles, initialValue, setFieldsValue]);

	const handleCopyIconClick = (index: number) => {
		if (popoverVisibleIndex === index) {
			setPopoverVisibleIndex(null);
			setSelectedPeriods([]);
			return;
		}
		const otherPeriodIndices = (budgetPerPeriod || [])
			.map((_, periodIndex) => periodIndex)
			.filter((periodIndex) => periodIndex !== index);
		setSelectedPeriods(otherPeriodIndices);
		setPopoverVisibleIndex(index);
	};

	const handlePopoverClose = useCallback(() => {
		setPopoverVisibleIndex(null);
		setSelectedPeriods([]);
	}, []);

	const handleVisibleChange = useCallback(
		(visible: boolean) => {
			if (!visible) {
				handlePopoverClose();
			}
		},
		[handlePopoverClose]
	);

	const handlePeriodToggle = (periodIndex: number) => {
		setSelectedPeriods((prev) =>
			prev.includes(periodIndex) ? prev.filter((i) => i !== periodIndex) : [...prev, periodIndex]
		);
	};

	const handleAllPeriodsToggle = () => {
		if (!budgetPerPeriod || popoverVisibleIndex === null) return;
		const otherPeriodIndices = budgetPerPeriod
			.map((_, periodIndex) => periodIndex)
			.filter((periodIndex) => periodIndex !== popoverVisibleIndex);

		if (selectedPeriods.length === otherPeriodIndices.length) {
			setSelectedPeriods([]);
		} else {
			setSelectedPeriods(otherPeriodIndices);
		}
	};

	const handleApply = () => {
		if (popoverVisibleIndex === null || !budgetPerPeriod) return;

		const sourceValue = nullSafeGetWithZero(`${popoverVisibleIndex}.budget`, budgetPerPeriod);
		const updatedPeriods = budgetPerPeriod.map((period, index) => {
			if (selectedPeriods.includes(index)) {
				return { ...period, budget: sourceValue };
			}
			return period;
		});

		setFieldsValue({ budgetPerPeriod: updatedPeriods });
		handlePopoverClose();
	};

	const { isAllSelected, isIndeterminate } = useMemo(() => {
		const totalOtherPeriods =
			budgetPerPeriod && popoverVisibleIndex !== null ? budgetPerPeriod.length - 1 : 0;
		const isAllSelected = totalOtherPeriods > 0 && selectedPeriods.length === totalOtherPeriods;
		const isIndeterminate =
			selectedPeriods.length > 0 && selectedPeriods.length < totalOtherPeriods;
		return { isAllSelected, isIndeterminate };
	}, [budgetPerPeriod, popoverVisibleIndex, selectedPeriods.length]);

	const getDateParts = (cycle) => {
		if (!cycle.startDate || !cycle.endDate) return { start: '', end: '' };
		return {
			start: getDateOnlyStartOfDayFromBEDateString(cycle.startDate).format('DD MMM YYYY'),
			end: getDateOnlyStartOfDayFromBEDateString(cycle.endDate).format('DD MMM YYYY'),
		};
	};

	const renderPopoverContent = (sourceIndex: number) => {
		if (!budgetPerPeriod) return null;

		return (
			<div style={{ minWidth: 290 }}>
				<div className="bodyregular border-b border-l-0 border-r-0 border-t-0 border-solid border-grey-5 px-3 py-2 text-character-secondary">
					Copy amount to:
				</div>
				<div>
					<div
						onClick={handleAllPeriodsToggle}
						className="mx-3 flex cursor-pointer items-center gap-2 border-b border-l-0 border-r-0 border-t-0 border-solid border-grey-4 py-[11px]"
					>
						<Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
						<span className="bodymedium text-character-primary">All Periods</span>
					</div>
					<div className="mt-2 flex flex-col gap-3">
						{budgetPerPeriod.map((cycle, index) => {
							if (index === sourceIndex) return null;
							const isChecked = selectedPeriods.includes(index);
							const dates = getDateParts(cycle);
							return (
								<div
									key={index}
									onClick={() => handlePeriodToggle(index)}
									className="mx-3 flex cursor-pointer items-center gap-2 py-[5px]"
								>
									<Checkbox checked={isChecked} />
									<span className="bodymedium text-black/85">Period {index + 1}</span>
									<span className="bodyregular text-character-secondary">{dates.start}</span>
									<span className="bodyregular text-character-secondary">-</span>
									<span className="bodyregular text-character-secondary">{dates.end}</span>
								</div>
							);
						})}
					</div>
					<div className="p-3">
						<Button
							type="primary"
							block
							onClick={handleApply}
							disabled={selectedPeriods.length === 0}
						>
							APPLY
						</Button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<Form.List name="budgetPerPeriod">
			{(fields) => (
				<>
					{fields.map(({ key, name, ...restField }, index) => {
						const cycle = nullSafeGet(`${name}`, budgetPerPeriod);
						const budgetValue = nullSafeGetWithZero(`${name}.budget`, budgetPerPeriod);
						const hasBudgetValue =
							budgetValue !== null && budgetValue !== undefined && budgetValue !== '';
						return cycle ? (
							<div
								key={key}
								className="flex w-full flex-row justify-between gap-4"
								style={{ alignItems: 'flex-start' }}
							>
								<strong className="mb-6" style={{ marginTop: 6 }}>
									Period {index + 1}
								</strong>
								<div className="mb-6" style={{ marginTop: 6 }}>
									{getCycleRangeText(cycle)}
								</div>
								<div
									className="flex flex-row gap-2"
									style={{ marginBottom: 24, alignItems: 'flex-start' }}
								>
									<Form.Item
										{...restField}
										name={[name, 'budget']}
										rules={[{ required: true, message: 'Enter a budget' }]}
										style={{ marginBottom: 0 }}
									>
										<InputNumber
											style={{ width: 150 }}
											placeholder="Budget"
											addonAfter={currency.id}
										/>
									</Form.Item>
									<Popover
										content={renderPopoverContent(index)}
										trigger={hasBudgetValue ? 'click' : []}
										visible={popoverVisibleIndex === index}
										onVisibleChange={handleVisibleChange}
										placement="bottomLeft"
										autoAdjustOverflow={false}
										align={{
											overflow: { adjustY: false, adjustX: false },
											offset: [1, -22],
										}}
										overlayClassName="copy-budget-popover"
										overlayInnerStyle={{
											borderRadius: 4,
											boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
											padding: 0,
										}}
										getPopupContainer={(trigger) => trigger.parentElement || document.body}
									>
										<div className="py-2.5">
											<CopyOutlined
												style={hasBudgetValue ? { color: '#531DAB' } : { color: '#D9D9D9' }}
												onClick={hasBudgetValue ? () => handleCopyIconClick(index) : undefined}
												className={cn(
													'size-[14px] transition-all duration-200',
													hasBudgetValue ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
												)}
											/>
										</div>
									</Popover>
								</div>
							</div>
						) : null;
					})}
				</>
			)}
		</Form.List>
	);
};

export default BudgetForEachCycleFormField;
